import React from 'react';
import styles from './CompanyCalendar.module.css';

function TwoWeekCalendar({ resourceData, hideKeys = false }) {
    if (!resourceData) {
        return null;
    }

    // Determine window start date: prefer resourceData.startDate (ISO), else today
    const parseISO = (s) => {
        if (!s) return null;
        const d = new Date(s);
        return isNaN(d.getTime()) ? null : d;
    };

    // Always anchor the view to the Monday of the current week, and show 14 days
    const todayInit = new Date();
    const mondayStart = new Date(todayInit);
    const dayOfWeek = mondayStart.getDay(); // 0=Sun,1=Mon,...
    const diffToMonday = (dayOfWeek === 0 ? -6 : 1 - dayOfWeek);
    mondayStart.setHours(0, 0, 0, 0);
    mondayStart.setDate(mondayStart.getDate() + diffToMonday);

    const startDate = mondayStart;
    const windowLength = 14;

    // Build days array containing date objects and helpers. Include an index (1-based)
    const daysArray = [...Array(windowLength).keys()].map(i => {
        const dt = new Date(startDate);
        dt.setDate(startDate.getDate() + i);
        const iso = dt.toISOString().slice(0, 10);
        return { index: i + 1, date: dt, iso, dayOfMonth: dt.getDate() };
    });

    const findSpecial = (dayObj) => {
        const list = resourceData.specialDays || [];
        return list.find(d => {
            if (!d) return false;
            const v = d.day;
            // 1) Prefer exact ISO date matches
            if (typeof v === 'string') {
                return v === dayObj.iso;
            }
            if (typeof v === 'number') {
                // 2) In the 2-week view, numeric values 1..14 represent column indexes
                if (v >= 1 && v <= 14) return v === dayObj.index;
                // 3) Larger numbers can be explicit day-of-month values
                if (v > 14 && v <= 31) return v === dayObj.dayOfMonth;
            }
            // Fallback no-match
            return false;
        });
    };

    // Today helper (local date, not UTC ISO) to avoid duplicate highlights
    const today = new Date();
    today.setHours(0,0,0,0);
    const isSameLocalDate = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

    const getDayClass = (dayObj) => {
        // Match main calendar behavior: render dataset-driven classes only.
        // Weekends/present-day shading is delivered through specialDays from API.
        const foundDay = findSpecial(dayObj);
        return foundDay ? foundDay.type : '';
    };

    const getDayClassText = (dayObj) => {
        const foundDay = findSpecial(dayObj);
        return foundDay ? `${foundDay.type}Text` : '';
    };

    const isBetweenDates = (d, start, end) => {
        if (!start || !end) return false;
        const t = d.getTime();
        return t >= start.getTime() && t <= end.getTime();
    };

    const renderBarsForCell = (resource, dayObj) => {
        const bars = resource.bars || [];
        const dayDate = dayObj.date;

        // Check bars that provide ISO start/end dates first (matches main calendar multi-day logic)
        const barByIsoRange = bars.find(bar => (bar.startDate || bar.start_iso) && (bar.endDate || bar.end_iso) && isBetweenDates(dayDate, parseISO(bar.startDate || bar.start_iso), parseISO(bar.endDate || bar.end_iso)));
        if (barByIsoRange) return <div style={{ background: barByIsoRange.color }} className={styles.fullBar}></div>;

        // Check numeric start/end ranges: support both day-of-month and index-based (1..N)
        const barByNumRange = bars.find(bar => typeof bar.start === 'number' && typeof bar.end === 'number' && ((dayObj.index > bar.start && dayObj.index < bar.end) || (dayObj.dayOfMonth > bar.start && dayObj.dayOfMonth < bar.end)) && bar.start !== bar.end);
        if (barByNumRange) return <div style={{ background: barByNumRange.color }} className={styles.fullBar}></div>;

        // Single full day (either iso or numeric index/day-of-month)
        const fullDayBar = bars.find(bar => ((bar.startDate && bar.endDate && bar.startDate === bar.endDate) || (bar.start === bar.end)) && ( (bar.day_part === 'FULL') || (!bar.day_part) ) &&
            ( (bar.startDate && bar.startDate === dayObj.iso) || (bar.start === dayObj.index) || (bar.end === dayObj.index) || (bar.start === dayObj.dayOfMonth) || (bar.end === dayObj.dayOfMonth) ) );
        if (fullDayBar) return <div style={{ background: fullDayBar.color }} className={styles.oneBar}></div>;

        // Start / end partials by numeric index or day-of-month
        const startBar = bars.find(bar => (bar.startDate && bar.startDate === dayObj.iso) || ((bar.start === dayObj.index || bar.start === dayObj.dayOfMonth) && bar.start !== bar.end));
        if (startBar) return <div style={{ background: startBar.color }} className={styles.startBar}></div>;

        const endBar = bars.find(bar => (bar.endDate && bar.endDate === dayObj.iso) || ((bar.end === dayObj.index || bar.end === dayObj.dayOfMonth) && bar.end !== bar.start));
        if (endBar) return <div style={{ background: endBar.color }} className={styles.endBar}></div>;

        // AM/PM handling: either use iso start or numeric start (behaves like main calendar)
        const halfDayBars = bars.filter(bar => {
            const startIso = bar.startDate || bar.start_iso;
            if (startIso) return startIso === dayObj.iso && (bar.day_part === 'AM' || bar.day_part === 'PM');
            if (typeof bar.start === 'number') {
                const s = Math.floor(bar.start);
                if (s >= 1 && s <= 31) {
                    return (s === dayObj.dayOfMonth || s === dayObj.index) && (bar.day_part === 'AM' || bar.day_part === 'PM');
                }
                return s === dayObj.index && (bar.day_part === 'AM' || bar.day_part === 'PM');
            }
            return false;
        });

        if (halfDayBars.length > 0) {
            return (
                <>
                    {halfDayBars.map(bar => (
                        <div
                            key={`${resource.name}-${dayObj.iso}-${bar.day_part}`}
                            style={{ background: bar.color }}
                            className={bar.day_part === 'AM' ? styles.AMbar : styles.PMbar}
                        />
                    ))}
                </>
            );
        }

        return null;
    };

    return (
        <div className={styles.companyHolidayCalendar}>
            <div className={styles.CalendarControlls}>
                <div style={{ fontWeight: 600 }}>
                    {/* Show range: start -> end */}
                    {(() => {
                        const startIso = daysArray[0].iso;
                        const endIso = daysArray[daysArray.length - 1].iso;
                        return `${startIso} → ${endIso}`;
                    })()}
                </div>
            </div>

            <table className={styles.CalendarTable}>
                <thead>
                    <tr>
                        <th style={{ border: 'none' }}></th>
                        {daysArray.map((dayObj) => (
                            <th key={`head-${dayObj.iso}`} className={styles[getDayClassText(dayObj)]}>{`${dayObj.date.toLocaleString(undefined, { weekday: 'short' })} ${dayObj.date.getDate()}`}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>&nbsp;</td>
                        {daysArray.map((dayObj) => (
                            <td key={`blank-${dayObj.iso}`} className={`${styles[getDayClass(dayObj)]}`}>&nbsp;</td>
                        ))}
                    </tr>
                    {(resourceData.resources || []).map((resource) => (
                        <tr key={resource.name}>
                            <td>{resource.name}</td>
                            {daysArray.map((dayObj) => (
                                <td key={`${resource.name}-${dayObj.iso}`} className={`${styles[getDayClass(dayObj)]}`}>
                                    {renderBarsForCell(resource, dayObj)}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>

            {!hideKeys && Array.isArray(resourceData.TableKeys) && resourceData.TableKeys.length > 0 && (
                <div className={styles.keys}>
                    {resourceData.TableKeys.map((key) => (
                        <div key={key.name} className={styles.keyBox}>
                            <div className={styles.key}>
                                <div style={{ background: key.color }} className={styles[key.type]}></div>
                                <div className={styles.keyName}>{key.name}</div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default TwoWeekCalendar;


